import { buildApiUrl, fetchJSON, validateRequiredParams } from './crmApi';

export interface DealInput {
  organization_id: string;
  title: string;
  amount?: number;
  stage_id: string;
  company_id?: string | null;
  contact_id?: string | null;
  close_date?: string;
  stage_probability?: number;
  forecast_category?: 'pipeline' | 'best' | 'commit' | 'closed';
  expected_close_quarter?: string;
  arr_amount?: number;
  projected_invoice_date?: string;
}

export interface Deal extends DealInput {
  id: string;
  owner_user_id: string;
  created_at: string;
  updated_at: string;
  weighted_amount?: number;
  pm_project_id?: string | null; // Link to Project Management project
}

export interface ListDealsParams {
  organization_id: string;
  stage_id?: string;
  stage_name?: string;
  company_id?: string;
  contact_id?: string;
  search?: string;
  limit?: number;
  offset?: number;
  forecast_category?: 'pipeline' | 'best' | 'commit' | 'closed';
  min_probability?: number;
  max_probability?: number;
}

const dealsService = {
  async list(params: ListDealsParams): Promise<{ data: Deal[]; count: number | null }>{
    validateRequiredParams(params, ['organization_id'], {
      service: 'dealsService.list'
    });
    const url = buildApiUrl('/api/crm/deals', params as any);
    return fetchJSON(url, {
      description: 'List deals',
      context: {
        organization_id: params.organization_id,
        stage_id: params.stage_id,
        stage_name: params.stage_name,
        company_id: params.company_id,
        contact_id: params.contact_id,
        search: params.search,
        limit: params.limit,
        offset: params.offset
      }
    });
  },
  async get(id: string, organization_id: string): Promise<{ data: Deal | null }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'dealsService.get'
    });
    const url = buildApiUrl(`/api/crm/deals/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      description: 'Get deal',
      context: { id, organization_id }
    });
  },
  async create(payload: DealInput): Promise<{ data: Deal }>{
    validateRequiredParams(payload, ['organization_id', 'title', 'stage_id'], {
      service: 'dealsService.create'
    });
    const url = buildApiUrl('/api/crm/deals');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create deal',
      context: { organization_id: payload.organization_id, stage_id: payload.stage_id }
    });
  },
  async update(id: string, payload: Partial<DealInput> & { organization_id: string }): Promise<{ data: Deal }>{
    validateRequiredParams({ id, ...payload }, ['id', 'organization_id'], {
      service: 'dealsService.update'
    });
    const url = buildApiUrl(`/api/crm/deals/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: { ...payload, id },
      description: 'Update deal',
      context: { id, organization_id: payload.organization_id }
    });
  },
  async remove(id: string, organization_id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'dealsService.remove'
    });
    const url = buildApiUrl(`/api/crm/deals/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete deal',
      context: { id, organization_id }
    });
  }
};

export default dealsService;
